import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from "../components/Spinner";
import BrandMark from "../components/BrandMark";
import { TRYON_SERVER_URL } from "../config";
import { useSelector } from "react-redux";
import axios from "axios";
import { useAuthentication } from "../Authentication";
import { selectAuthenticated } from "../selectors";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const { isAuthenticated } = useAuthentication();
    const authenticated = useSelector(selectAuthenticated);

    // Get token from URL query params (will be used when backend is integrated)
    const token = searchParams.get('token');

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [spinner, setSpinner] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);

    const checkAuthentication = useCallback(async () => {
        try {
            const userAuthenticated = await isAuthenticated();
            if (userAuthenticated) {
                if (location.state !== null) {
                    navigate(location.state.path);
                } else {
                    navigate("/");
                }
            }
        } catch (error) {
            console.error('Error checking authentication:', error);

            if (error.message && error.message.toLowerCase().includes('network')) {
                console.error('Network error, redirecting to login page');
                navigate("/signin");
            }
        }
    }, [isAuthenticated, navigate, location.state]);

    useEffect(() => {
        console.log("Authenticated:", authenticated);
        if (!authenticated) {
            checkAuthentication();
        }
    }, []);

    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const toggleConfirmPasswordVisibility = () => {
        setConfirmPasswordVisible(!confirmPasswordVisible);
    };

    const validatePassword = (pwd) => {
        // Minimum 8 characters, at least one letter and one number, special characters allowed
        const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
        return passwordRegex.test(pwd);
    };

    const submitForm = async () => {
        setErrorMessage("");
        setShowError(false);

        // Validation
        if (!password || !confirmPassword) {
            setErrorMessage("Please enter both password fields");
            setShowError(true);
            setSpinner(false);
            return;
        }

        if (password.length < 8) {
            setErrorMessage("Password must be at least 8 characters long");
            setShowError(true);
            setSpinner(false);
            return;
        }

        if (!validatePassword(password)) {
            setErrorMessage("Password must be at least 8 characters long and contain at least one letter and one number. Special characters are allowed.");
            setShowError(true);
            setSpinner(false);
            return;
        }

        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match");
            setShowError(true);
            setSpinner(false);
            return;
        }

        console.log("token:", token);

        // Validate token is present
        if (!token) {
            setErrorMessage("Invalid reset link. Please request a new password reset link.");
            setShowError(true);
            setSpinner(false);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('token', token);
            formData.append('password', password);
            
            const response = await axios.post(
                `${TRYON_SERVER_URL}/api/v1/user/password/reset/confirm/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status === 200 || response.status === 201) {
                setResetSuccess(true);
                setSpinner(false);
                console.log('Password reset successfully:', response.data);
            } else {
                throw new Error("Failed to reset password. Please try again.");
            }
        } catch (error) {
            console.error('Error resetting password:', error?.response?.data);
            const errorMessage = error?.response?.data?.message || 
                                error?.response?.data?.error || 
                                error?.message || 
                                "Failed to reset password. Please try again.";
            setErrorMessage(errorMessage);
            setShowError(true);
            setSpinner(false);
        }
    };

    const handleResetPassword = (e) => {
        e.preventDefault();
        setSpinner(true);
        submitForm();
    };

    return (
        <div className="flex h-screen flex-col overflow-y-auto bg-ground">
            <div className="flex min-h-full w-full items-start justify-center px-4 py-8">
                <div className="mx-auto flex w-full max-w-lg justify-center">
                    <div className="w-full rounded-2xl bg-white p-6 shadow-[0_0_0_1px_var(--hairline)]">
                        <div className="mb-6 flex items-center justify-center gap-3">
                            <BrandMark alt="" className="h-9 w-9 shrink-0 object-contain" />
                            <div className="font-Mulish text-lg font-semibold text-secondary">
                                TryOn AI
                            </div>
                        </div>

                        {!resetSuccess ? (
                            <form action="#">
                                <h2 className="font-Mulish text-lg font-semibold text-secondary">
                                    Reset password
                                </h2>
                                <p className="mt-1 font-Mulish text-sm text-graphite">
                                    Choose a new password.
                                </p>

                                <div className="mt-4">
                                    <label htmlFor="password" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                        New password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={passwordVisible ? "text" : "password"}
                                            name="password"
                                            id="password"
                                            placeholder="New password"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setErrorMessage("");
                                                setShowError(false);
                                            }}
                                            className="w-full rounded-lg border-0 bg-white px-3 py-2.5 pr-10 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            required
                                        />
                                        {passwordVisible ? <Eye onClick={togglePasswordVisibility} aria-label="Hide" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" /> : <EyeOff onClick={togglePasswordVisibility} aria-label="Show" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" />}
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <label htmlFor="confirm-password" className="mb-1.5 block font-Mulish text-sm text-graphite">
                                        Confirm password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={confirmPasswordVisible ? "text" : "password"}
                                            name="confirm-password"
                                            id="confirm-password"
                                            placeholder="Confirm password"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                setErrorMessage("");
                                                setShowError(false);
                                            }}
                                            className="w-full rounded-lg border-0 bg-white px-3 py-2.5 pr-10 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite focus:outline-none focus:ring-2 focus:ring-primary-200"
                                            required
                                        />
                                        {confirmPasswordVisible ? <Eye onClick={toggleConfirmPasswordVisibility} aria-label="Hide" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" /> : <EyeOff onClick={toggleConfirmPasswordVisibility} aria-label="Show" className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 cursor-pointer text-gray-400" />}
                                    </div>
                                </div>

                                {showError && (
                                    <p className="mt-4 border-l-2 border-primary-700 pl-3 font-Mulish text-sm text-secondary">
                                        {errorMessage}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    onClick={handleResetPassword}
                                    className={`mt-4 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 font-Mulish text-sm font-semibold ${
                                        spinner
                                            ? "bg-primary-100 text-primary-800"
                                            : "bg-primary-700 text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                    }`}
                                >
                                    {spinner ? <Spinner /> : "Reset password"}
                                </button>

                                <div className="mt-4 text-center">
                                    <span className="font-Mulish text-sm text-graphite">
                                        Remember your password?{" "}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/signin")}
                                        className="font-Mulish text-sm text-primary-700 hover:underline"
                                    >
                                        Sign in
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="rounded-2xl bg-primary-50 p-6 text-center">
                                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary-700 shadow-[0_0_0_1px_var(--hairline)]">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h2 className="font-Mulish text-lg font-semibold text-secondary">
                                    Password updated
                                </h2>
                                <p className="mt-1 font-Mulish text-sm text-graphite">
                                    You can sign in with the new password.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/signin")}
                                    className="mt-4 w-full rounded-full bg-primary-700 px-4 py-2.5 font-Mulish text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(190,49,32,0.65)]"
                                >
                                    Sign in
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

